// MaturaSimilarIsoscelesTriangleClosedTask.js
// Zadanie maturalne zamknięte: podobne trójkąty w trójkącie równoramiennym.

import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import ABCDInput from "../../../../components/inputs/ABCDInput";

const LETTERS = ["A", "B", "C", "D"];

const CASES = [
  { equalSide: 4, base: 3 },
  { equalSide: 5, base: 4 },
  { equalSide: 6, base: 3 },
  { equalSide: 8, base: 6 },
  { equalSide: 9, base: 6 },
  { equalSide: 10, base: 5 },
  { equalSide: 10, base: 8 },
  { equalSide: 12, base: 9 },
];

const randInt = (min, max) => Math.floor(Math.random() * (max - min + 1)) + min;
const pick = (arr) => arr[randInt(0, arr.length - 1)];

function gcd(a, b) {
  let x = Math.abs(a);
  let y = Math.abs(b);
  while (y) [x, y] = [y, x % y];
  return x || 1;
}

function frac(num, den = 1) {
  const d = gcd(num, den);
  return { num: num / d, den: den / d };
}

function fracKey(value) {
  return `${value.num}/${value.den}`;
}

function latexValue(value) {
  const { num, den } = value;
  if (den === 1) return String(num);

  const scaled = (num * 100) / den;
  if (Number.isInteger(scaled)) {
    const text = (scaled / 100).toLocaleString("pl-PL", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    });
    return text.replace(",", "{,}");
  }

  return `\\frac{${num}}{${den}}`;
}

function fractionLatex(value) {
  if (value.den === 1) return String(value.num);
  return `\\frac{${value.num}}{${value.den}}`;
}

function shuffle(items) {
  const arr = [...items];
  for (let i = arr.length - 1; i > 0; i -= 1) {
    const j = randInt(0, i);
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}

function makeOptions(correct, equalSide, base) {
  const values = [];
  const add = (value) => {
    if (value.num <= 0) return;
    const key = fracKey(value);
    if (!values.some((v) => fracKey(v) === key)) values.push(value);
  };

  add(correct);
  add(frac(correct.num * 4 - correct.den, correct.den * 4));
  add(frac(correct.num * 4 + correct.den, correct.den * 4));
  add(frac(base, 1));
  add(frac(equalSide - base, 1));
  add(frac(equalSide, 1));

  let step = 1;
  while (values.length < 4) {
    add(frac(correct.num + step * correct.den, correct.den));
    step += 1;
  }

  return shuffle(values.slice(0, 4)).map((value, idx) => ({
    label: LETTERS[idx],
    value,
    latex: latexValue(value),
    isCorrect: fracKey(value) === fracKey(correct),
  }));
}

function LatexLabel({
  x,
  y,
  math,
  color = "currentColor",
  width = 46,
  height = 24,
  anchor = "middle",
  fontSize = 14,
}) {
  const boxX = anchor === "start" ? x : anchor === "end" ? x - width : x - width / 2;
  const textAlign = anchor === "start" ? "left" : anchor === "end" ? "right" : "center";

  return (
    <foreignObject x={boxX} y={y - height / 2} width={width} height={height}>
      <div style={{ color, fontSize, lineHeight: 1.2, textAlign }}>
        <InlineMath math={String(math)} />
      </div>
    </foreignObject>
  );
}

function SimilarIsoscelesTriangleDiagram({ equalSide, base }) {
  const size = 330;
  const height = 285;
  const COLORS = {
    base: "currentColor",
    accent: "#f59e0b",
  };

  const rawA = { x: 0, y: 0 };
  const rawB = { x: base, y: 0 };
  const rawH = Math.sqrt(Math.max(equalSide * equalSide - (base * base) / 4, 1));
  const rawC = { x: base / 2, y: -rawH };
  const t = (base * base) / (equalSide * equalSide);
  const rawD = {
    x: rawB.x + t * (rawC.x - rawB.x),
    y: rawB.y + t * (rawC.y - rawB.y),
  };

  const minX = Math.min(rawA.x, rawB.x, rawC.x, rawD.x);
  const maxX = Math.max(rawA.x, rawB.x, rawC.x, rawD.x);
  const minY = Math.min(rawA.y, rawB.y, rawC.y, rawD.y);
  const maxY = Math.max(rawA.y, rawB.y, rawC.y, rawD.y);
  const padding = 42;
  const scale = Math.min(
    (size - padding * 2) / (maxX - minX || 1),
    (height - padding * 2) / (maxY - minY || 1)
  );
  const drawWidth = (maxX - minX) * scale;
  const drawHeight = (maxY - minY) * scale;
  const left = (size - drawWidth) / 2;
  const top = (height - drawHeight) / 2;

  const map = (p) => ({
    x: left + (p.x - minX) * scale,
    y: top + (p.y - minY) * scale,
  });

  const A = map(rawA);
  const B = map(rawB);
  const C = map(rawC);
  const D = map(rawD);
  const centroid = { x: (A.x + B.x + C.x) / 3, y: (A.y + B.y + C.y) / 3 };

  const outsideLabel = (P, label, offset = 16) => {
    const vx = P.x - centroid.x;
    const vy = P.y - centroid.y;
    const len = Math.hypot(vx, vy) || 1;
    const x = P.x + (vx / len) * offset;
    const y = P.y + (vy / len) * offset;
    const anchor = vx > 8 ? "start" : vx < -8 ? "end" : "middle";
    return { x, y, label, anchor };
  };

  const segmentLabel = (P, Q, label, offset = 15) => {
    const mid = { x: (P.x + Q.x) / 2, y: (P.y + Q.y) / 2 };
    const dx = Q.x - P.x;
    const dy = Q.y - P.y;
    const len = Math.hypot(dx, dy) || 1;
    const n1 = { x: -dy / len, y: dx / len };
    const n2 = { x: dy / len, y: -dx / len };
    const c1 = { x: mid.x + n1.x * offset, y: mid.y + n1.y * offset };
    const c2 = { x: mid.x + n2.x * offset, y: mid.y + n2.y * offset };
    const d1 = Math.hypot(c1.x - centroid.x, c1.y - centroid.y);
    const d2 = Math.hypot(c2.x - centroid.x, c2.y - centroid.y);
    return d1 >= d2 ? { ...c1, label } : { ...c2, label };
  };

  const labels = [outsideLabel(A, "A"), outsideLabel(B, "B"), outsideLabel(C, "C")];
  const dLabel = { x: D.x + 13, y: D.y - 2, label: "D", anchor: "start" };
  const abLabel = segmentLabel(A, B, base, 16);
  const acLabel = segmentLabel(A, C, equalSide, 16);

  return (
    <svg width={size} height={height} viewBox={`0 0 ${size} ${height}`} style={{ display: "block", width: "100%", maxWidth: size, height: "auto", margin: "0 auto" }}>
      <line x1={A.x} y1={A.y} x2={B.x} y2={B.y} stroke={COLORS.base} strokeWidth="2.3" />
      <line x1={A.x} y1={A.y} x2={C.x} y2={C.y} stroke={COLORS.base} strokeWidth="2.3" />
      <line x1={B.x} y1={B.y} x2={C.x} y2={C.y} stroke={COLORS.base} strokeWidth="2.3" />
      <line x1={A.x} y1={A.y} x2={D.x} y2={D.y} stroke={COLORS.base} strokeWidth="2.1" />

      <circle cx={A.x} cy={A.y} r="3.8" fill={COLORS.base} />
      <circle cx={B.x} cy={B.y} r="3.8" fill={COLORS.base} />
      <circle cx={C.x} cy={C.y} r="3.8" fill={COLORS.base} />
      <circle cx={D.x} cy={D.y} r="3.2" fill={COLORS.base} />

      <LatexLabel x={abLabel.x} y={abLabel.y} math={abLabel.label} color={COLORS.accent} />
      <LatexLabel x={acLabel.x} y={acLabel.y} math={acLabel.label} color={COLORS.accent} />

      {labels.map((item) => (
        <LatexLabel key={item.label} x={item.x} y={item.y} math={item.label} anchor={item.anchor} />
      ))}
      <LatexLabel x={dLabel.x} y={dLabel.y} math="D" anchor={dLabel.anchor} />
    </svg>
  );
}

function generateMay2025Original() {
  const equalSide = 4;
  const base = 3;
  const answer = frac(9, 4);
  const values = [frac(2), answer, frac(5, 2), frac(3)];
  const options = values.map((value, idx) => ({
    label: LETTERS[idx],
    value,
    latex: latexValue(value),
    isCorrect: idx === 1,
  }));

  return {
    type: "MaturaSimilarIsoscelesTriangleClosedTask",
    data: { equalSide, base, answer, options, correctIndex: 1 },
    source: {
      variant: "original",
      exam: "MATURA",
      session: "2025_MAJ",
      taskNumber: 20,
    },
  };
}

export function generateTask(generationOptions = {}) {
  if (
    generationOptions.variant === "original" &&
    generationOptions.session === "2025_MAJ"
  ) {
    return generateMay2025Original();
  }

  const { equalSide, base } = pick(CASES);
  const answer = frac(base * base, equalSide);
  const options = makeOptions(answer, equalSide, base);
  const correctIndex = options.findIndex((option) => option.isCorrect);

  return {
    type: "MaturaSimilarIsoscelesTriangleClosedTask",
    data: {
      equalSide,
      base,
      answer,
      options,
      correctIndex,
    },
  };
}

export function renderTask({ data }) {
  const { equalSide, base, options } = data;

  return (
    <div className="space-y-3" style={{ textAlign: "left" }}>
      <p>
        <strong>Zadanie maturalne - zamknięte (0-1 pkt).</strong>
      </p>

      <p>
        W trójkącie równoramiennym <InlineMath math="ABC" /> dane są:{" "}
        <InlineMath math={`|AC|=|BC|=${equalSide}`} /> i <InlineMath math={`|AB|=${base}`} />.
        Na boku <InlineMath math="BC" />, między punktami <InlineMath math="B" /> i{" "}
        <InlineMath math="C" />, wybrano taki punkt <InlineMath math="D" />, że trójkąty{" "}
        <InlineMath math="ABC" /> i <InlineMath math="BDA" /> są podobne (zobacz rysunek).
      </p>

      <SimilarIsoscelesTriangleDiagram equalSide={equalSide} base={base} />

      <p>
        Dokończ zdanie. Wybierz właściwą odpowiedź spośród podanych.
      </p>
      <p>
        Odcinek <InlineMath math="BD" /> ma długość
      </p>

      <div style={{ marginTop: 8 }}>
        {options.map((option) => (
          <div key={option.label} style={{ marginBottom: 8 }}>
            <InlineMath math={`${option.label}.\\; ${option.latex}`} />
          </div>
        ))}
      </div>
    </div>
  );
}

export function renderInput({ data }, userAnswer, onChange, disabled) {
  const choiceIndex = userAnswer && typeof userAnswer.choiceIndex === "number" ? userAnswer.choiceIndex : null;
  const letterOptions = data.options.map((option, idx) => ({
    value: idx,
    label: option.label,
    textNode: option.label,
  }));

  const handleChange = (idx) => {
    const option = data.options[idx];
    onChange?.({
      choiceIndex: idx,
      label: option.label,
      latex: option.latex,
      value: option.value,
    });
  };

  return <ABCDInput options={letterOptions} value={choiceIndex} onChange={handleChange} disabled={disabled} />;
}

export function checkAnswer({ data }, userAnswer) {
  if (!userAnswer || typeof userAnswer.choiceIndex !== "number") return false;
  return userAnswer.choiceIndex === data.correctIndex;
}

export function renderAnswer({ data }) {
  const correct = data.options?.[data.correctIndex];
  if (!correct) return null;
  return (
    <div style={{ marginTop: 6 }}>
      <InlineMath math={`${correct.label}.\\; ${correct.latex}`} />
    </div>
  );
}

export function renderUserAnswer(userAnswer) {
  if (!userAnswer || typeof userAnswer.choiceIndex !== "number" || !userAnswer.label || !userAnswer.latex) {
    return <i>(brak odpowiedzi)</i>;
  }

  return (
    <div style={{ marginTop: "0.5rem" }}>
      <span>Twoja odpowiedź: </span>
      <InlineMath math={`${userAnswer.label}.\\; ${userAnswer.latex}`} />
    </div>
  );
}

export function renderExplanation({ data }) {
  const { equalSide, base, answer } = data;
  const answerFraction = fractionLatex(answer);
  const answerLatex = latexValue(answer);
  const decimalPart = answerLatex === answerFraction ? "" : ` = ${answerLatex}`;

  return (
    <div style={{ textAlign: "left", lineHeight: 1.7 }}>
      <p>
        <strong>Krok 1.</strong> Trójkąt <InlineMath math="ABC" /> jest równoramienny, bo{" "}
        <InlineMath math={`|AC|=|BC|=${equalSide}`} />. Trójkąty <InlineMath math="ABC" /> i{" "}
        <InlineMath math="BDA" /> są podobne, więc możemy ustawić odpowiadające wierzchołki jako{" "}
        <InlineMath math={"ABC \\sim DBA"} />.
      </p>

      <p>
        <strong>Krok 2.</strong> Wtedy bok <InlineMath math="BC" /> odpowiada bokowi{" "}
        <InlineMath math="BA" />, więc skala małego trójkąta do dużego wynosi:
      </p>
      <BlockMath math={`k=\\frac{|BA|}{|BC|}=\\frac{${base}}{${equalSide}}`} />

      <p>
        <strong>Krok 3.</strong> Bok <InlineMath math="BD" /> odpowiada bokowi{" "}
        <InlineMath math="AB" />, dlatego:
      </p>
      <BlockMath math={`|BD|=k\\cdot |AB|`} />
      <BlockMath math={`|BD|=\\frac{${base}}{${equalSide}}\\cdot ${base}= ${answerFraction}${decimalPart}`} />

      <p>
        Zatem odcinek <InlineMath math="BD" /> ma długość <InlineMath math={answerLatex} />.
      </p>
    </div>
  );
}
